import { useGame } from "@/game/GameContext";
import StatBar from "@/components/StatBar";
import PixelButton from "@/components/PixelButton";
import AshLogo from "@/components/AshLogo";
import { statLabel } from "@/game/gameLogic";
import { RotateCcw, ExternalLink } from "lucide-react";

export default function EndScreen() {
  const { state, dispatch } = useGame();
  const r = state.result;
  if (!r) return null;
  const startingBudget = state.wheelOutcome?.budget || 0;

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-6">
          <AshLogo variant="full" width={220} className="mx-auto mb-3" />
          <div className="pixel-font text-[10px] text-white/70 tracking-widest">
            YEAR ONE COMPLETE
          </div>
          <h2 className="pixel-font text-2xl md:text-3xl text-[#F58634] mt-2" data-testid="end-title">
            {r.title.toUpperCase()}
          </h2>
        </div>

        <div className="pixel-panel mb-4">
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <div className="bg-[#0F172A] text-white border-4 border-black p-3 text-center">
              <div className="pixel-font text-[10px] text-white/70">FINAL SCORE</div>
              <div className="vt323 text-5xl text-[#F58634] tabular-nums" data-testid="final-score">
                {r.score}
              </div>
              <div className="vt323 text-lg text-white/70">/ 100</div>
            </div>
            <div className="bg-[#0F172A] text-white border-4 border-black p-3 text-center">
              <div className="pixel-font text-[10px] text-white/70">STRONGEST</div>
              <div className="pixel-font text-sm text-emerald-300 mt-2">{r.strongest}</div>
            </div>
            <div className="bg-[#0F172A] text-white border-4 border-black p-3 text-center">
              <div className="pixel-font text-[10px] text-white/70">WEAKEST</div>
              <div className="pixel-font text-sm text-red-300 mt-2">{r.weakest}</div>
            </div>
          </div>

          <p className="text-base leading-relaxed mb-4" data-testid="end-recap">
            {r.recap}
          </p>

          <div className="bg-[#0F172A] p-3 border-4 border-black space-y-2">
            {["awareness", "trust", "leads", "energy", "systems"].map((k) => (
              <StatBar key={k} label={statLabel(k)} value={state.stats[k]} statKey={k} />
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-[#F4F0E6] border-4 border-black p-3">
              <div className="pixel-font text-[10px] text-[#1A1A1A]/70">STARTING BUDGET</div>
              <div className="vt323 text-2xl tabular-nums">${startingBudget.toLocaleString()}</div>
            </div>
            <div className="bg-[#F4F0E6] border-4 border-black p-3">
              <div className="pixel-font text-[10px] text-[#1A1A1A]/70">ENDING BUDGET</div>
              <div className="vt323 text-2xl tabular-nums">${state.stats.budget.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="grid gap-3" data-testid="end-actions">
          <a
            href="https://www.ash-interactive.com/clarity-checkup"
            target="_blank"
            rel="noreferrer"
            data-testid="clarity-checkup-button"
            className="pixel-btn w-full text-center justify-center"
          >
            Marketing Clarity Checkup
            <ExternalLink className="w-[1em] h-[1em]" strokeWidth={3} aria-hidden="true" />
          </a>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PixelButton
              data-testid="play-again-button"
              variant="secondary"
              onClick={() => dispatch({ type: "RESTART" })}
            >
              <RotateCcw className="w-[1em] h-[1em]" strokeWidth={3} aria-hidden="true" />
              <span>Play Again</span>
            </PixelButton>
            <a
              href="https://www.ash-interactive.com"
              target="_blank"
              rel="noreferrer"
              data-testid="visit-ash-end"
              className="pixel-btn pixel-btn-dark text-center justify-center"
            >
              <span>Visit ASH</span>
              <ExternalLink className="w-[1em] h-[1em]" strokeWidth={3} aria-hidden="true" />
            </a>
          </div>
        </div>

        <p className="text-center vt323 text-lg text-white/60 mt-6">
          Think you can beat your score? Try the trail again with a different character, budget or business type.
        </p>
      </div>
    </div>
  );
}
